import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { Calendar, ChevronDown, ChevronUp, X } from 'lucide-react'
import { DateTime } from 'luxon'
import { datePattern, getDisplayTimeZone, timePattern, weekStartJsDay } from '../../lib/format'

/**
 * Date-range picker matching the visual + interaction model of the
 * `TaskDatePopover` used in the Space List view. Two pill triggers
 * sit inline (Start date / End date) so the admin can see both values
 * at a glance without opening anything. Clicking either pill opens a
 * single floating popover containing a preset list on the left, a
 * month calendar in the middle, and an optional 15-minute time-slot
 * column that unfurls on the right when "Add time" is clicked.
 *
 * The popover renders through a portal under document.body so it
 * escapes any parent `overflow: hidden` (the Maintenance scope card
 * has rounded corners with clipped overflow). Position is fixed
 * to the trigger row's bottom edge, clamped to the viewport so the
 * popover never falls off-screen on narrow windows.
 *
 * Values flow as `yyyy-MM-ddTHH:mm` strings — the same shape the
 * native `<input type="datetime-local">` accepts — so callers can
 * round-trip through `isoToLocalInput` / `localInputToIso` helpers
 * already used by the surrounding form.
 *
 * Props:
 *   value:    { start: string|null, end: string|null }
 *   onChange: (next) => void
 *   error?:   string  // surface below the trigger row in red
 */

// ── Date helpers (local-time only — never touch UTC inside the
//    picker, the form-level helpers do the UTC ↔ local conversion). ──

function startOfDay(d) {
  const x = new Date(d)
  x.setHours(0, 0, 0, 0)
  return x
}

function addDays(d, n) {
  const x = new Date(d)
  x.setDate(x.getDate() + n)
  return x
}

function addMonths(d, n) {
  const x = new Date(d)
  x.setMonth(x.getMonth() + n)
  return x
}

/** Next occurrence of `dow` (0=Sun..6=Sat) strictly after `from`,
 *  or `from` itself when `inclusive` is true. Mirrors the SpacePage
 *  helper so the preset behaviour matches verbatim. */
function nextDow(from, dow, inclusive = false) {
  const x = startOfDay(from)
  const cur = x.getDay()
  let delta = (dow - cur + 7) % 7
  if (delta === 0 && !inclusive) delta = 7
  return addDays(x, delta)
}

function pad(n) {
  return String(n).padStart(2, '0')
}

// Labels follow the person's date format and 12/24-hour clock. The picker's
// Dates are timezone-free wall-clock values, so they're read by their parts.
function fmtDateShort(d) {
  return DateTime.fromObject(
    { year: d.getFullYear(), month: d.getMonth() + 1, day: d.getDate() },
    { locale: 'en-GB' },
  ).toFormat(datePattern())
}

function fmtTime(d) {
  return DateTime.fromObject({ hour: d.getHours(), minute: d.getMinutes() }, { locale: 'en-GB' })
    .toFormat(timePattern())
    .replace(/\b(am|pm)\b/i, (m) => m.toUpperCase())
}

/** Today (00:00) as a wall-clock Date in the person's display zone — the zone
 *  the surrounding form converts these values from/to. */
function todayInZone() {
  const t = DateTime.now().setZone(getDisplayTimeZone() || 'local')
  return new Date(t.year, t.month - 1, t.day)
}

// Weekday headers in Sunday-first order (JS getDay()), rotated to the
// person's first day of the week.
const DOW_SHORT = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa']
function weekdayHeaders() {
  const first = weekStartJsDay()
  return Array.from({ length: 7 }, (_, i) => DOW_SHORT[(first + i) % 7])
}

function fmtDow(d) {
  return d.toLocaleDateString('en-GB', { weekday: 'short' })
}

/** Parse the `yyyy-MM-ddTHH:mm` shape datetime-local emits back into a
 *  local-time Date. Returns null for empty / unparseable input. */
function parseLocal(s) {
  if (!s) return null
  const m = /^(\d{4})-(\d{2})-(\d{2})(?:T(\d{2}):(\d{2}))?/.exec(s)
  if (!m) return null
  const [, y, mo, d, h, min] = m
  return new Date(
    Number(y),
    Number(mo) - 1,
    Number(d),
    Number(h ?? 0),
    Number(min ?? 0),
  )
}

/** Serialise a local-time Date back to `yyyy-MM-ddTHH:mm`. */
function toLocalString(d) {
  if (!d) return null
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(
    d.getHours(),
  )}:${pad(d.getMinutes())}`
}

function hasNonZeroTime(d) {
  return Boolean(d) && (d.getHours() !== 0 || d.getMinutes() !== 0)
}

/** 6×7 day matrix for the given month, starting on the person's first day
 *  of the week (Monday or Sunday). */
function monthMatrix(viewMonth) {
  const first = new Date(viewMonth.getFullYear(), viewMonth.getMonth(), 1)
  const lead = (first.getDay() - weekStartJsDay() + 7) % 7
  const start = addDays(first, -lead)
  return Array.from({ length: 42 }, (_, i) => addDays(start, i))
}

/** 15-minute slots for the day, labelled in the person's clock. */
function timeSlots() {
  const out = []
  for (let h = 0; h < 24; h += 1) {
    for (let m = 0; m < 60; m += 15) {
      const d = new Date()
      d.setHours(h, m, 0, 0)
      out.push({ hh: h, mm: m, label: fmtTime(d) })
    }
  }
  return out
}

/** Preset list — same eight entries the SpacePage picker offers. */
function presetDefs() {
  const today = todayInZone()
  return [
    {
      key: 'today',
      label: 'Today',
      date: () => today,
      hint: () => fmtDow(today),
    },
    {
      key: 'later',
      label: 'Later',
      date: () => {
        const x = new Date(today)
        x.setHours(18, 0, 0, 0)
        return x
      },
      hint: () => fmtTime(new Date(2000, 0, 1, 18, 0)),
    },
    {
      key: 'tomorrow',
      label: 'Tomorrow',
      date: () => addDays(today, 1),
      hint: () => fmtDow(addDays(today, 1)),
    },
    {
      key: 'thisWeekend',
      label: 'This weekend',
      date: () => nextDow(today, 6, true),
      hint: () => fmtDow(nextDow(today, 6, true)),
    },
    {
      key: 'nextWeek',
      label: 'Next week',
      date: () => nextDow(today, 1),
      hint: () => fmtDateShort(nextDow(today, 1)),
    },
    {
      key: 'nextWeekend',
      label: 'Next weekend',
      date: () => addDays(nextDow(today, 6, true), 7),
      hint: () => fmtDateShort(addDays(nextDow(today, 6, true), 7)),
    },
    {
      key: 'twoWeeks',
      label: '2 weeks',
      date: () => addDays(today, 14),
      hint: () => fmtDateShort(addDays(today, 14)),
    },
    {
      key: 'fourWeeks',
      label: '4 weeks',
      date: () => addDays(today, 28),
      hint: () => fmtDateShort(addDays(today, 28)),
    },
  ]
}

export function DateRangePicker({ value, onChange, error }) {
  const [open, setOpen] = useState(false)
  // Which pill the calendar / preset / time-slot clicks land on.
  const [active, setActive] = useState('start')
  // Which pill, if any, has its time-slot column expanded.
  const [timeFor, setTimeFor] = useState(null)
  const triggerRef = useRef(null)
  const popoverRef = useRef(null)
  const [coords, setCoords] = useState(null)

  const startObj = parseLocal(value?.start)
  const endObj = parseLocal(value?.end)

  // Anchor the calendar to whichever side has a value, falling back
  // to today. Lifted into state so flipping months doesn't snap back
  // every time the user picks a date.
  const [viewMonth, setViewMonth] = useState(() => {
    const anchor = startObj ?? endObj ?? todayInZone()
    return new Date(anchor.getFullYear(), anchor.getMonth(), 1)
  })

  // Position the popover directly under the trigger row each time it
  // opens. Clamp horizontally so the right edge never overruns the
  // viewport — the popover is 600px wide and easily wider than a
  // narrow tablet pane. Never flips above the trigger; the popover
  // is short enough not to need that, and flipping makes the open
  // direction unpredictable.
  useLayoutEffect(() => {
    if (!open) return
    function reposition() {
      const r = triggerRef.current?.getBoundingClientRect()
      if (!r) return
      const popoverW = 600
      const rightGutter = 16
      const leftGutter = 8
      const gap = 6
      const margin = 12
      let left = r.left
      if (left + popoverW > window.innerWidth - rightGutter) {
        left = Math.max(leftGutter, window.innerWidth - rightGutter - popoverW)
      }
      // Flip above the trigger when there isn't room below, and cap the
      // height to the available space (applied with overflow-y: auto on
      // the element) so the calendar is always fully reachable near a
      // viewport edge.
      // Cap to the popover's natural height so a flipped-up popover hugs the
      // trigger instead of ballooning to the top of the viewport (matches the
      // fix in SpacePage's TaskDatePopover). Prefer opening below.
      const naturalH = 440
      const spaceBelow = window.innerHeight - r.bottom - gap - margin
      const spaceAbove = r.top - gap - margin
      const openUp = spaceBelow < 260 && spaceAbove > spaceBelow
      const space = openUp ? spaceAbove : spaceBelow
      const maxHeight = Math.max(220, Math.floor(Math.min(space, naturalH)))
      const top = openUp
        ? Math.max(margin, r.top - gap - maxHeight)
        : r.bottom + gap
      setCoords({ top, left, maxHeight })
    }
    reposition()
    // Re-position on scroll / resize so the popover follows the
    // trigger when the page moves underneath it.
    window.addEventListener('scroll', reposition, true)
    window.addEventListener('resize', reposition)
    return () => {
      window.removeEventListener('scroll', reposition, true)
      window.removeEventListener('resize', reposition)
    }
  }, [open])

  // Dismiss on outside click + ESC. Click-outside ignores the trigger
  // row so toggling via the pill doesn't immediately re-open from
  // dismissal — the pill's own onClick handles open/close.
  useEffect(() => {
    if (!open) return
    function onDown(e) {
      if (popoverRef.current && popoverRef.current.contains(e.target)) return
      if (triggerRef.current && triggerRef.current.contains(e.target)) return
      setOpen(false)
      setTimeFor(null)
    }
    function onKey(e) {
      if (e.key === 'Escape') {
        setOpen(false)
        setTimeFor(null)
      }
    }
    document.addEventListener('mousedown', onDown)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  const matrix = useMemo(() => monthMatrix(viewMonth), [viewMonth])
  const today = todayInZone()
  const presets = useMemo(() => presetDefs(), [])
  const slots = useMemo(() => timeSlots(), [])

  function emit(nextStart, nextEnd) {
    onChange({ start: toLocalString(nextStart), end: toLocalString(nextEnd) })
  }

  function openOn(field) {
    setActive(field)
    setTimeFor(null)
    setOpen(true)
  }

  function pickDate(d) {
    // Preserve existing time on the active side. Picking a different
    // day on a value that already has 09:00 keeps the 09:00 instead
    // of stomping back to midnight.
    const current = active === 'start' ? startObj : endObj
    const next = new Date(d)
    if (current) {
      next.setHours(
        current.getHours(),
        current.getMinutes(),
        current.getSeconds(),
        current.getMilliseconds(),
      )
    }
    if (active === 'start') {
      emit(next, endObj)
      // Auto-advance to End so the second date is one click away.
      if (!endObj) setActive('end')
    } else {
      emit(startObj, next)
    }
  }

  function pickTime(field, hh, mm) {
    const current = field === 'start' ? startObj : endObj
    const base = current ?? todayInZone()
    const next = new Date(base)
    next.setHours(hh, mm, 0, 0)
    if (field === 'start') emit(next, endObj)
    else emit(startObj, next)
  }

  function clearField(field) {
    if (field === 'start') emit(null, endObj)
    else emit(startObj, null)
    if (timeFor === field) setTimeFor(null)
  }

  function toggleTimeFor(field) {
    setActive(field)
    setOpen(true)
    setTimeFor((cur) => (cur === field ? null : field))
  }

  // Pre-snap milestone timestamps for fast in-loop comparisons.
  const startMs = startObj ? startOfDay(startObj).getTime() : null
  const endMs = endObj ? startOfDay(endObj).getTime() : null
  const rangeLow =
    startMs !== null && endMs !== null ? Math.min(startMs, endMs) : null
  const rangeHigh =
    startMs !== null && endMs !== null ? Math.max(startMs, endMs) : null
  const todayMs = today.getTime()

  // Trigger summary chip — shows the current Start / End at a glance
  // and prompts to set a schedule when empty. Mirrors the compact
  // calendar-icon trigger SpacePage uses on task rows: small, neutral,
  // and reveals the full editing UI in the popover when clicked.
  const summaryText = (() => {
    if (!startObj && !endObj) return 'Schedule start & end'
    const left = startObj ? fmtDateShort(startObj) : '—'
    const right = endObj ? fmtDateShort(endObj) : '—'
    return `${left}  →  ${right}`
  })()
  const summaryHasValue = Boolean(startObj || endObj)

  return (
    <div>
      {/* Trigger — single compact button. Click opens the popover,
       *  which is the actual editing surface and contains the pills
       *  + calendar + time column (matching SpacePage TaskDatePopover
       *  exactly). Kept compact so the scope card stays tight when
       *  no schedule is set. */}
      <button
        ref={triggerRef}
        type="button"
        onClick={() => {
          // Open on whichever side is empty first so the admin's next
          // click lands somewhere useful. If both are empty, default
          // to Start. If both are set, default to Start (it's the
          // first one they're most likely to want to edit).
          if (!startObj) openOn('start')
          else if (!endObj) openOn('end')
          else openOn('start')
        }}
        className={
          'inline-flex items-center gap-2 px-3 py-1.5 rounded-md border text-sm cursor-pointer ' +
          (open
            ? 'border-[color:var(--color-accent)] bg-[color:var(--color-accent-bg)] text-[color:var(--color-accent)]'
            : summaryHasValue
              ? 'border-[color:var(--color-rule)] bg-[color:var(--color-card)] text-[color:var(--color-ink)] hover:border-[color:var(--color-accent)]'
              : 'border-[color:var(--color-rule)] bg-[color:var(--color-card)] text-[color:var(--color-ink-muted)] hover:border-[color:var(--color-accent)] hover:text-[color:var(--color-ink)]')
        }
      >
        <Calendar size={14} />
        <span className="tabular-nums">{summaryText}</span>
      </button>

      {error ? (
        <p className="mt-1 text-xs text-[color:var(--color-priority-urgent)]">
          {error}
        </p>
      ) : null}

      {open && coords
        ? createPortal(
            <div
              ref={popoverRef}
              style={{
                position: 'fixed',
                top: coords.top,
                left: coords.left,
                zIndex: 60,
                // 600px on desktop, but never wider than the viewport on
                // mobile (the calendar half would otherwise clip off-screen).
                // The body below stacks presets above the calendar under `sm`.
                width: 'min(600px, calc(100vw - 16px))',
                // Cap to measured space + scroll internally so the
                // calendar is always fully reachable near a viewport edge.
                // Inline overflowY beats the class's `overflow-hidden`
                // on the vertical axis; horizontal stays hidden (rounding).
                maxHeight: coords.maxHeight,
                overflowY: 'auto',
              }}
              className="rounded-md border border-[color:var(--color-rule)] bg-[color:var(--color-card)] shadow-2xl text-[color:var(--color-ink)] overflow-hidden"
            >
              {/* Pills — inside the popover, matching SpacePage's
               *  TaskDatePopover. Tap either pill to switch which
               *  side the calendar / preset / time-slot clicks
               *  apply to. */}
              <div className="flex items-center gap-2 p-2 border-b border-[color:var(--color-rule)]">
                <DatePill
                  label={startObj ? fmtDateShort(startObj) : 'Start date'}
                  time={hasNonZeroTime(startObj) ? fmtTime(startObj) : null}
                  active={active === 'start'}
                  empty={!startObj}
                  onSelect={() => {
                    setActive('start')
                    setTimeFor(null)
                  }}
                  onClear={() => clearField('start')}
                  onAddTime={() => toggleTimeFor('start')}
                />
                <DatePill
                  label={endObj ? fmtDateShort(endObj) : 'End date'}
                  time={hasNonZeroTime(endObj) ? fmtTime(endObj) : null}
                  active={active === 'end'}
                  empty={!endObj}
                  onSelect={() => {
                    setActive('end')
                    setTimeFor(null)
                  }}
                  onClear={() => clearField('end')}
                  onAddTime={() => toggleTimeFor('end')}
                />
              </div>

              {/* Body — presets beside the calendar on desktop; stacked
               *  (presets above calendar) on mobile so the 600px two-column
               *  layout doesn't overflow a phone viewport. */}
              <div className="flex flex-col sm:flex-row">
                {/* Presets — fixed width so longer hints
                 *  ("Next weekend  06/06/2026") fit on one line
                 *  without overlapping the calendar grid. */}
                <div className="w-full sm:w-64 shrink-0 overflow-hidden border-b sm:border-b-0 sm:border-r border-[color:var(--color-rule)] py-1">
                  {presets.map((p) => (
                    <button
                      key={p.key}
                      type="button"
                      onClick={() => pickDate(p.date())}
                      className="w-full flex items-center justify-between gap-2 px-3 py-1.5 text-sm hover:bg-[color:var(--color-card-hover)] cursor-pointer"
                    >
                      <span className="text-[color:var(--color-ink)] truncate">
                        {p.label}
                      </span>
                      <span className="text-xs text-[color:var(--color-ink-muted)] tabular-nums shrink-0">
                        {p.hint()}
                      </span>
                    </button>
                  ))}
                </div>

                {/* Calendar */}
                <div className="flex-1 p-2">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-sm font-semibold text-[color:var(--color-ink-strong)]">
                      {viewMonth.toLocaleDateString('en-GB', {
                        month: 'long',
                        year: 'numeric',
                      })}
                    </p>
                    <div className="flex items-center gap-1">
                      <button
                        type="button"
                        onClick={() => {
                          const t = todayInZone()
                          setViewMonth(
                            new Date(t.getFullYear(), t.getMonth(), 1),
                          )
                        }}
                        className="text-xs text-[color:var(--color-ink-muted)] hover:text-[color:var(--color-ink)] px-2 py-0.5 cursor-pointer"
                        title="Jump to today's month"
                      >
                        Today
                      </button>
                      <button
                        type="button"
                        onClick={() =>
                          setViewMonth(addMonths(viewMonth, -1))
                        }
                        aria-label="Previous month"
                        className="p-1 rounded text-[color:var(--color-ink-muted)] hover:text-[color:var(--color-ink)] hover:bg-[color:var(--color-card-hover)] cursor-pointer"
                      >
                        <ChevronUp size={14} />
                      </button>
                      <button
                        type="button"
                        onClick={() => setViewMonth(addMonths(viewMonth, 1))}
                        aria-label="Next month"
                        className="p-1 rounded text-[color:var(--color-ink-muted)] hover:text-[color:var(--color-ink)] hover:bg-[color:var(--color-card-hover)] cursor-pointer"
                      >
                        <ChevronDown size={14} />
                      </button>
                    </div>
                  </div>

                  <div className="grid grid-cols-7 gap-1 text-sm text-[color:var(--color-ink-muted)] mb-0.5">
                    {weekdayHeaders().map((d) => (
                      <div key={d} className="text-center py-0.5">
                        {d}
                      </div>
                    ))}
                  </div>

                  <div className="grid grid-cols-7 gap-1">
                    {matrix.map((d) => {
                      const dayMs = startOfDay(d).getTime()
                      const isPast = dayMs < todayMs
                      const isToday = dayMs === todayMs
                      const isStart = startMs !== null && dayMs === startMs
                      const isEnd = endMs !== null && dayMs === endMs
                      const isInRange =
                        rangeLow !== null &&
                        rangeHigh !== null &&
                        dayMs > rangeLow &&
                        dayMs < rangeHigh
                      const isOtherMonth = d.getMonth() !== viewMonth.getMonth()

                      let cellClass =
                        'h-7 rounded text-sm transition-colors relative ' +
                        (isPast ? 'cursor-not-allowed ' : 'cursor-pointer ')
                      if (isStart || isEnd) {
                        cellClass +=
                          'bg-[color:var(--color-accent)] text-white font-semibold hover:brightness-110 '
                      } else if (isInRange) {
                        cellClass +=
                          'bg-[color:var(--color-accent-bg)] text-[color:var(--color-accent-dim)] hover:bg-[color:var(--color-accent-bg)] '
                      } else if (isToday) {
                        cellClass +=
                          'ring-1 ring-inset ring-[color:var(--color-accent)] text-[color:var(--color-accent)] font-semibold hover:bg-[color:var(--color-card-hover)] '
                      } else if (isOtherMonth) {
                        cellClass +=
                          'text-[color:var(--color-ink-subtle)] hover:bg-[color:var(--color-card-hover)] '
                      } else if (isPast) {
                        cellClass +=
                          'text-[color:var(--color-ink-subtle)] hover:bg-[color:var(--color-card-hover)] '
                      } else {
                        cellClass +=
                          'text-[color:var(--color-ink)] hover:bg-[color:var(--color-card-hover)] '
                      }
                      // Past dates can't be selected (the button is `disabled`
                      // below) — keep them clearly faded, with no hover restore.
                      if (isPast) cellClass += 'opacity-40 '

                      if (isStart && endMs !== null && dayMs < endMs) {
                        cellClass = cellClass.replace('rounded ', 'rounded-l ')
                      } else if (isEnd && startMs !== null && dayMs > startMs) {
                        cellClass = cellClass.replace('rounded ', 'rounded-r ')
                      } else if (isInRange) {
                        cellClass = cellClass.replace('rounded ', 'rounded-none ')
                      }

                      const ariaLabel = isStart
                        ? `Start date ${fmtDateShort(d)}`
                        : isEnd
                          ? `End date ${fmtDateShort(d)}`
                          : isInRange
                            ? `In range ${fmtDateShort(d)}`
                            : fmtDateShort(d)

                      return (
                        <button
                          key={d.toISOString()}
                          type="button"
                          aria-label={ariaLabel}
                          aria-pressed={isStart || isEnd}
                          disabled={isPast}
                          title={
                            isPast ? 'Past dates cannot be selected.' : undefined
                          }
                          onClick={() => {
                            // Block past dates — never commit one.
                            if (!isPast) pickDate(d)
                          }}
                          className={cellClass}
                        >
                          {d.getDate()}
                        </button>
                      )
                    })}
                  </div>
                </div>

                {/* Time column — unfurls on "Add time" for the active
                 *  pill. Scrolls within itself so the popover's
                 *  overall height stays bounded. */}
                {timeFor ? (
                  <div className="w-32 border-l border-[color:var(--color-rule)] py-1 overflow-auto max-h-[320px]">
                    <p className="px-3 py-1 text-xs uppercase tracking-wider font-semibold text-[color:var(--color-ink-muted)]">
                      {timeFor === 'start' ? 'Start time' : 'End time'}
                    </p>
                    {slots.map((t) => {
                      const cur = timeFor === 'start' ? startObj : endObj
                      const selected =
                        cur &&
                        cur.getHours() === t.hh &&
                        cur.getMinutes() === t.mm
                      return (
                        <button
                          key={`${t.hh}:${t.mm}`}
                          type="button"
                          onClick={() => pickTime(timeFor, t.hh, t.mm)}
                          className={
                            'w-full text-left px-3 py-1 text-sm cursor-pointer ' +
                            (selected
                              ? 'bg-[color:var(--color-accent-bg)] text-[color:var(--color-accent)]'
                              : 'text-[color:var(--color-ink)] hover:bg-[color:var(--color-card-hover)]')
                          }
                        >
                          {t.label}
                        </button>
                      )
                    })}
                  </div>
                ) : null}
              </div>
            </div>,
            document.body,
          )
        : null}
    </div>
  )
}

/** Pill tab — the visual contract matches SpacePage's `DatePill`
 *  exactly so the two pickers feel like the same primitive: leading
 *  calendar icon, label OR formatted date, optional small time chip,
 *  X to clear, "Add time" / "Edit time" link. */
function DatePill({
  label,
  time,
  active,
  empty,
  onSelect,
  onClear,
  onAddTime,
}) {
  return (
    <div
      role="button"
      onClick={onSelect}
      className={
        'flex-1 flex items-center justify-between gap-2 px-2.5 py-1.5 rounded border text-sm cursor-pointer ' +
        (active
          ? 'border-[color:var(--color-accent)] bg-[color:var(--color-accent-bg)] text-[color:var(--color-accent)]'
          : 'border-[color:var(--color-rule)] bg-[color:var(--color-card)] text-[color:var(--color-ink)] hover:border-[color:var(--color-accent)]')
      }
    >
      <span className="flex items-center gap-1.5 min-w-0 truncate">
        <Calendar size={12} />
        <span className="truncate">{label}</span>
        {time ? <span className="text-xs opacity-70 ml-1">{time}</span> : null}
      </span>
      <span className="flex items-center gap-1 shrink-0">
        {empty ? null : (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation()
              onClear()
            }}
            className="text-[color:var(--color-ink-muted)] hover:text-[color:var(--color-priority-urgent)] cursor-pointer"
            aria-label="Clear"
          >
            <X size={12} />
          </button>
        )}
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation()
            onAddTime()
          }}
          className="text-xs text-[color:var(--color-ink-muted)] hover:text-[color:var(--color-ink)] cursor-pointer"
        >
          {time ? 'Edit Time' : 'Add Time'}
        </button>
      </span>
    </div>
  )
}
